import React, { useState, useCallback } from 'react';
import { Input, Alert, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import RoundedModal from '@components/roundedModal/roundedModal';
import useTranslation from '@hooks/useTranslation';
import deploymentApi from '@src/api/deployment/deploymentApi';
import { CreateEnvironmentRequest, Environment } from '@src/api/deployment/deploymentApi'; // Ensure types are exported in api file or assume structure if not
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import styles from './CreateEnvironmentModal.module.css';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: (environment: Environment) => void;
  initialData?: Environment;
}

interface TagRow {
  id: string;
  key: string;
  value: string;
}

const CreateEnvironmentModal: React.FC<Props> = ({ visible, onClose, onSuccess, initialData }) => {
  const t = useTranslation('CreateEnvironmentModal');
  const tCommon = t.withBaseKey('CommonWords');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [isPrivate, setIsPrivate] = useState(initialData?.private ?? false);
  const [tags, setTags] = useState<TagRow[]>(() => {
    if (initialData?.tags) {
      return Object.entries(initialData.tags).map(([key, value], index) => ({
        id: `tag-${index}`,
        key,
        value,
      }));
    }
    return [];
  });

  const resetForm = useCallback(() => {
    setName(initialData?.name || '');
    setDescription(initialData?.description || '');
    setIsPrivate(initialData?.private ?? false);
    setTags(() => {
      if (initialData?.tags) {
        return Object.entries(initialData.tags).map(([key, value], index) => ({
          id: `tag-${index}`,
          key,
          value,
        }));
      }
      return [];
    });
    setError(null);
  }, [initialData]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleAddTag = useCallback(() => {
    setTags(prev => [
      ...prev,
      { id: Date.now().toString(), key: '', value: '' }
    ]);
  }, []);

  const handleRemoveTag = useCallback((id: string) => {
    setTags(prev => prev.filter(tag => tag.id !== id));
  }, []);

  const handleTagChange = useCallback((id: string, field: 'key' | 'value', value: string) => {
    setTags(prev => prev.map(tag => 
      tag.id === id ? { ...tag, [field]: value } : tag
    ));
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('Error.NameRequired'));
      return;
    }

    // Validate tags: check for empty keys
    const hasEmptyKeys = tags.some(tag => !tag.key.trim());
    if (hasEmptyKeys) {
      setError(t('Error.TagKeyRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tagsRecord: Record<string, string> = {};
      tags.forEach(tag => {
        if (tag.key.trim()) {
          tagsRecord[tag.key.trim()] = tag.value;
        }
      });

      const request: CreateEnvironmentRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        tags: Object.keys(tagsRecord).length > 0 ? tagsRecord : undefined,
        private: isPrivate,
      };

      let updatedEnvironment: Environment;
      if (initialData) {
        updatedEnvironment = await deploymentApi.updateEnvironment(initialData.id, request);
      } else {
        updatedEnvironment = await deploymentApi.createEnvironment(request);
      }
      
      onSuccess(updatedEnvironment);
      handleClose();
    } catch (err) {
      console.error('Failed to create environment:', err);
      setError(err instanceof Error ? err.message : t('Error.CreateFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoundedModal
      visible={visible}
      onCancel={handleClose}
      onOk={handleSubmit}
      title={initialData ? t('Title.Edit') : t('Title')}
      width={600}
      footerClassName={styles.Footer}
      okBtnLabel={initialData ? tCommon('Update') : t('CreateButton')}
      cancelBtnLabel={tCommon('Cancel')}
      loadingOkBtn={loading}
      disableOkButton={loading || !name.trim()}
      closeOnMaskClick={false}
    >
      <div className={styles.Container}>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            className={styles.ErrorAlert}
          />
        )}

        <div className={styles.FormSection}>
          <label className={styles.Label}>
            {t('Label.Name')} <span className={styles.Required}>*</span>
          </label>
          <Input
            placeholder={t('Placeholder.Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className={styles.FormSection}>
          <label className={styles.Label}>
            {t('Label.Description')} <span className={styles.Optional}>({tCommon('Optional')})</span>
          </label>
          <Input.TextArea
            placeholder={t('Placeholder.Description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            disabled={loading}
          />
        </div>

        <div className={styles.FormSection}>
          <label className={styles.Label}>
            {t('Label.Private')} <span className={styles.Optional}>({tCommon('Optional')})</span>
          </label>
          <div className={styles.SwitchRow}>
            <KemuSwitch
              checked={isPrivate}
              onChange={(checked) => setIsPrivate(checked)}
              size="default"
              disabled={loading}
            />
            <span className={styles.SwitchDescription}>
              {t('Label.Private.Description')}
            </span>
          </div>
        </div>

        <div className={styles.FormSection}>
          <label className={styles.Label}>
            {t('Label.Tags')} <span className={styles.Optional}>({tCommon('Optional')})</span>
          </label>
          
          <div className={styles.TagsContainer}>
            {tags.map((tag) => (
              <div key={tag.id} className={styles.TagRow}>
                <Input
                  className={styles.TagInput}
                  placeholder={t('Placeholder.TagKey')}
                  value={tag.key}
                  onChange={(e) => handleTagChange(tag.id, 'key', e.target.value)}
                  disabled={loading}
                />
                <Input
                  className={styles.TagInput}
                  placeholder={t('Placeholder.TagValue')}
                  value={tag.value}
                  onChange={(e) => handleTagChange(tag.id, 'value', e.target.value)}
                  disabled={loading}
                />
                <Button 
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveTag(tag.id)}
                  className={styles.RemoveBtn}
                  disabled={loading}
                />
              </div>
            ))}
            
            <Button
              type="dashed"
              onClick={handleAddTag}
              icon={<PlusOutlined />}
              className={styles.AddTagBtn}
              disabled={loading}
            >
              {t('Button.AddTag')}
            </Button>
          </div>
        </div>
      </div>
    </RoundedModal>
  );
};

export default CreateEnvironmentModal;

